import { PROJECT_STATUS_LABELS, PROJECT_STATUS_TONES } from '@/shared/constants/statuses';
import type { StatusTone } from '@/shared/constants/statuses';
import { TONE_COLORS } from '@/shared/lib/tones';
import type { CalendarItem, ProjectStatus } from '@/shared/types/domain';

/**
 * **Matiz diz quão firme é. Preenchimento diz se é seu. E é só isso**
 * (spec-calendar-ux.md §4, decisão 92).
 *
 * Dois canais, duas perguntas. A tentação de acrescentar um terceiro (cor por
 * coordenação, peso por posse, borda por espaço) é a tentação de fazer a grade
 * responder o que o **filtro** responde melhor.
 *
 * ⚠️ **A cor deixou de significar PROCEDÊNCIA.** Até a
 * [ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md) o chip era pintado
 * pela fonte (Agenda Fixa cinza, Projetos azul, Meus verde, Propostas âmbar) —
 * quatro cores respondendo "de onde isto veio?", pergunta que a tela deixou de
 * fazer quando as fontes sumiram. Agora ele é pintado pela SITUAÇÃO, com os
 * mesmos tons que o crachá do painel já usa (`PROJECT_STATUS_TONES`). Nada de
 * paleta paralela para o calendário: um projeto "Em análise" é ciano na fila,
 * na tabela e na grade.
 */

/**
 * Quatro matizes, e é o teto — acima disso a grade vira confete e a cor deixa de
 * responder qualquer coisa. São exatamente os quatro status vivos:
 *
 * | Situação | Tom | Por quê |
 * |---|---|---|
 * | Em análise | `info` | é o estado de espera, e o crachá dele já é esse |
 * | Ajustes solicitados (só para o dono) | `warning` | é o único que COBRA algo de quem olha |
 * | Aprovado | `success` | é compromisso |
 * | Concluído | `neutral` | é passado |
 *
 * `danger` está no mapa porque `StatusTone` o tem, não porque apareça: `rejected`
 * e `cancelled` nunca chegam à grade (ADR-015 §3). Deixá-lo de fora exigiria um
 * tipo parcial e um `?? fallback` — que é como uma situação nova entraria na
 * tela pintada de cinza, sem ninguém decidir nada.
 */
/**
 * ⚠️ **A PALETA saiu daqui** e mora em
 * [shared/lib/tones.ts](../../../shared/lib/tones.ts) desde que a aba Início
 * passou a desenhar gráfico — `shared/` não pode importar de `features/`
 * (ADR-006), e o gráfico precisava das mesmas cinco matizes. O que ficou neste
 * arquivo é o que sempre foi dele: **como a grade COMPÕE** um chip a partir do
 * tom, e o argumento de por que preenchimento significa acesso.
 *
 * As três funções abaixo não mudaram de comportamento uma vírgula: `chipFilled`
 * e `chipOutline` são os antigos `filled` e `outline`, e `solid` é o antigo
 * `dot` — que ganhou nome novo porque agora também é a área de barra dos
 * gráficos, e chamá-la de "ponto" ali seria descrever a forma de um consumidor
 * só.
 */

/**
 * O tom de um item da grade.
 *
 * Ocorrência da agenda fixa é `neutral` porque é pano de fundo: ela ocupa o
 * prédio toda semana e não deve competir com o que muda. Hoje ela nunca chega
 * (§3.3) — o ramo existe para o dia em que chegar.
 */
export function toneOf(item: CalendarItem): StatusTone {
  return item.kind === 'occurrence' ? 'neutral' : PROJECT_STATUS_TONES[item.project.status];
}

/**
 * As classes do chip.
 *
 * **O preenchimento não é decoração: ele PREVÊ o que vai acontecer no clique.**
 * Contorno abre uma folha com quatro campos; preenchido abre o projeto inteiro.
 * Prever o resultado do clique antes de clicar é a definição operacional de
 * interface previsível — e é por isso que os dois canais não podem ser
 * misturados com um terceiro significado.
 */
export function chipClassOf(item: CalendarItem): string {
  const chip = TONE_COLORS[toneOf(item)];
  return item.access === 'full' ? chip.chipFilled : `bg-transparent border ${chip.chipOutline}`;
}

/** O filete de cor da lista do dia e da folha — a mesma matiz, sem o chip. */
export function dotClassOf(item: CalendarItem): string {
  return TONE_COLORS[toneOf(item)].solid;
}

/**
 * O mesmo filete, a partir da situação sozinha — é o que a folha de filtros usa
 * ao lado de cada caixa de "Situação" (§18).
 *
 * Ele existe para que a lista de situações e a grade se expliquem uma à outra:
 * quem marca "Aprovado" vê ali o verde que vai procurar na grade. A cor continua
 * sem ser o único portador — o rótulo está do lado.
 */
export function statusDotClass(status: ProjectStatus): string {
  return TONE_COLORS[PROJECT_STATUS_TONES[status]].solid;
}

/**
 * A situação por extenso.
 *
 * ⚠️ **Acessibilidade: cor nunca é o único portador.** Esta frase vai para o
 * `title` do chip, para a folha e para a lista do dia; o filtro de situação
 * nomeia as mesmas palavras. Quem não distingue ciano de âmbar continua lendo
 * "Em análise" e "Aprovado".
 */
export function statusLabelOf(item: CalendarItem): string {
  return item.kind === 'occurrence' ? 'Agenda fixa' : PROJECT_STATUS_LABELS[item.project.status];
}
